<script setup lang="ts">
import { onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import {
  useTenantStore,
  useAuthStore,
  useThemeStore,
  useReportsFanOut,
} from '@sera/core';
import Toast from 'primevue/toast';
import { guardStripeGatewayAnomaly } from '../plugins/sentry';
// Local appReady ref mirrors the store's computed value. We use a local ref
// (rather than a computed proxy) so the template's v-if pin is stable and
// doesn't flicker if the store reference is reassigned during HMR.
const tenantStore = useTenantStore();
const authStore = useAuthStore();
const reportsFanOut = useReportsFanOut();
useThemeStore(); // initializes store on every page load so saved theme class is applied immediately
const appReady = ref(false);
// quick 260520-hon: if tenantStore.init() rejects (customizations API down or
// the subdomain doesn't resolve to a tenant), surface an error screen instead
// of leaving every route as a permanent blank page. The widget route at
// /schedule-appointment hits this hardest because it has no auth-flow recovery
// path — every other route at least gets pushed to /login on auth failure.
const tenantLoadFailed = ref(false);
const router = useRouter();

// Respect prefers-reduced-motion at the component layer. When reduce is set we
// render the RouterView without the <Transition> wrapper so Vue's enter/leave
// lifecycle never runs and no shell-fade-* classes are ever applied. The CSS
// guards in main.css remain as belt-and-suspenders for any nested transitions.
//
// Why this matters (see .planning/debug/webkit-shell-fade-lingering.md):
//   Vue's <Transition> applies *-enter-from/*-enter-active synchronously, then
//   removes *-from after a double rAF. On webkit-in-Docker the next test step
//   (page.evaluate reading classList) ran before that double rAF, capturing
//   the transient class state. Gating the Transition itself removes the race
//   at the source — no transition runs, no classes applied, no race.
const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

// onBeforeMount runs after pinia is installed (main.ts line 17) and before
// the first render. RouterView is gated below so no route mounts until
// both tenant config and auth session have been resolved.
onBeforeMount(async () => {
  // Order matters (Risk 4 in 03-RESEARCH.md):
  // 1. Tenant first — apiClient base URL relies on tenant subdomain;
  //    GA4/Pendo loader scripts inject here too.
  // quick 260520-hon: wrap the whole boot in try/catch — auth depends on a
  // resolved tenant subdomain, so any failure in the tenant or dev-override
  // path means we cannot safely run restoreSession either. Bail the whole
  // sequence and surface the tenant-failure screen.
  try {
    await tenantStore.init();
    // PODZ-1655 / PODZ-1752: 'stripe' is a dead-code gatewayType (no tenant
    // uses it). If the resolved tenant config carries it, fire a fingerprinted
    // Sentry config-anomaly alert. The guard lives in the app sentry plugin
    // (not libs/core), so it is invoked here at boot — once, with the resolved
    // gatewayType. No-op for the real 'gpi'/'stax' gateways.
    guardStripeGatewayAnomaly(tenantStore.config?.gatewayType ?? '');
    if (import.meta.env.DEV) {
      const devSlug = localStorage.getItem('sera:dev-tenant');
      if (devSlug) {
        const { DEV_TENANTS } = await import('../dev/tenants');
        const t = DEV_TENANTS[devSlug];
        const current = tenantStore.config;
        if (t && current) {
          // Overlay ONLY the visual fields the dev switcher controls
          // (colors, logo, display name); preserve everything else from the
          // real init() response. Critically, `tenantSlug` is NOT overlaid —
          // it stays the real tenant (e.g. icemanplumbing) so the dev switcher
          // never repoints anything keyed off the slug. The embedded schedule
          // widget targets `<tenant>.<env>.api.sera.tech` via its `tenant`
          // attribute (EmbeddedScheduleView reads config.tenantSlug), so
          // rewriting the slug here would 404 the widget against a tenant that
          // may not exist on the current env. The dev switcher is visuals-only.
          tenantStore.applyBranding({
            ...current,
            primaryColor: t.primary,
            primaryHover: t.primary,
            primaryLight: t.primary,
            secondaryColor: t.secondary,
            logoUrl: t.logo,
            tenantName: t.name,
          });
        }
      }
    }
    // 2. Auth restore — apiClient is now ready; if token in localStorage,
    //    rehydrate user state (Pendo identify happens inside restoreSession).
    await authStore.restoreSession();
    // 3. Render gate — only flip after both stores have settled.
    appReady.value = true;
    // 4. Fire the consumer-report fan-out for nav badges. NOT awaited —
    //    the post-login redirect must not wait on three reports. Each
    //    store handles its own errors. Anonymous routes (no token, no
    //    user) skip the fetch since the report endpoints 401. See
    //    .planning/followups/nav-badges-and-report-fan-out.md.
    if (authStore.isAuthenticated) {
      void reportsFanOut.fetchAll();
    }
  } catch (err) {
    tenantLoadFailed.value = true;
    if (import.meta.env.DEV) {
      console.error('[App] tenantStore.init failed:', err);
    }
  }
});

function reload() {
  window.location.reload();
}

function onSessionExpired(e: Event) {
  // A CustomEvent dispatched without a detail payload (a different dispatcher
  // of auth:session-expired, or a future refactor) would throw on destructure
  // and break the redirect to login. This path must never fail.
  const detail = (e as CustomEvent<{ redirect?: string }>).detail;
  // Idempotency guard (PODZ-1704): if a prior event in this same tick already
  // landed us on /login, do not fire another replace. router.replace updates
  // window.location synchronously, but route.path lags by a microtask, so we
  // match against window.location here. Without this, multiple in-flight 401s
  // nest ?redirect=/login?redirect=... wrappers around the original redirect.
  if (window.location.pathname === '/login') return;
  const redirect = detail?.redirect ?? '';
  router.replace(redirect ? `/login?redirect=${redirect}` : '/login');
}

onMounted(() =>
  window.addEventListener('auth:session-expired', onSessionExpired),
);
onUnmounted(() =>
  window.removeEventListener('auth:session-expired', onSessionExpired),
);
</script>

<template>
  <Toast position="top-right" />
  <div
    v-if="tenantLoadFailed"
    data-testid="tenant-load-failed"
    class="min-h-dvh flex flex-col items-center justify-center bg-canvas p-6 text-center"
    role="alert"
  >
    <p class="text-lg font-semibold text-foreground">
      We're having trouble loading the app.
    </p>
    <p class="text-sm text-muted mt-2">Please refresh in a moment.</p>
    <button
      type="button"
      class="mt-6 px-4 py-2 rounded-md bg-primary text-primary-text"
      @click="reload"
    >
      Reload
    </button>
  </div>
  <RouterView v-if="appReady" v-slot="{ Component }">
    <Transition v-if="!prefersReducedMotion" name="shell-fade">
      <component :is="Component" />
    </Transition>
    <component :is="Component" v-else />
  </RouterView>
</template>
